"use client";

import { LoanOptionsPage } from "@/sanity/sanity.types";
import Card, { CardStyle } from "../common/card/card";
import GridColumn from "../common/grid-column/grid-column";
import GridRow from "../common/grid-row/grid-row";
import SuperTitle from "../common/supertitle/supertitle";
import ProductArticles from "./product/product-articles/product-articles";
import ProductFooter from "./product/product-footer/product-footer";
import css from "./products.module.scss";
import imageUrl from "@/sanity/utilities/imageUrl";
import Image from "@/components/common/image/image";
import { ArticleData } from "@/sanity/interfaces";
import { usePerformance } from "@/contexts/performance-context";
import { useEffect } from "react";

interface IProductsProps {
  data: LoanOptionsPage;
  latestArticles: ArticleData[];
  beforeQueryTime: number;
  afterQueryTime: number;
}

const Products = (props: IProductsProps) => {
  const { data, latestArticles, beforeQueryTime, afterQueryTime } = props;
  const { ctaHelp, heroBadges, productsGrid } = data;
  const { checkTimeElapsed } = usePerformance();

  useEffect(() => {
    checkTimeElapsed(beforeQueryTime, "Products page before query started");
    checkTimeElapsed(afterQueryTime, "Products page after query completed");
  }, []);

  return (
    <div className={css.products}>
      {productsGrid && (
        <GridRow>
          <GridColumn className={css.productsHeader}>
            {heroBadges?.[0] && (
              <div className="flex justify-start md:justify-center">
                <SuperTitle className={css.productsSupertitle}>
                  {heroBadges[0].image?.asset && (
                    <Image
                      src={imageUrl(heroBadges[0].image?.asset)
                        .height(18)
                        .url()}
                      alt={heroBadges[0].label ?? ""}
                      className="mr-2"
                      width={90}
                      height={18}
                    />
                  )}
                  {heroBadges[0].label}
                </SuperTitle>
              </div>
            )}
            <h1 className={css.productsTitle}>{productsGrid.heading}</h1>
            <p className={css.productsDescription}>{productsGrid.subheading}</p>
          </GridColumn>
          {productsGrid.items?.map((product, index) => (
            <GridColumn key={index} lg={4}>
              <Card
                description={product.summary}
                imgUrl={
                  product.icon?.asset
                    ? imageUrl(product.icon.asset).width(48).url()
                    : undefined
                }
                linkLabel={product.cta?.link?.label}
                linkUrl={product.cta?.link?.href}
                style={CardStyle.Secondary}
                title={product.shortLabel ?? ""}
                hoverEffect={true}
                iconHasBackground={true}
                imgWidth={48}
                imgHeight={48}
              />
            </GridColumn>
          ))}
        </GridRow>
      )}
      {latestArticles && <ProductArticles data={latestArticles} />}
      {ctaHelp && <ProductFooter data={ctaHelp} />}
    </div>
  );
};

export default Products;
